import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { SERVICES } from "@/data/services";
import PageShell from "@/components/PageShell";
import SectionTitle from "@/components/SectionTitle";
import { useSEO } from "@/hooks/useSEO";

const Services = () => {
  const { t, lang } = useLanguage();
  useSEO({
    title: t("services_page.title"),
    description: t("services_page.sub"),
    lang,
  });

  return (
    <PageShell dataTestId="services-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-10">
        <SectionTitle
          overline={t("services_page.overline")}
          title={t("services_page.title")}
          sub={t("services_page.sub")}
          testId="services-title"
        />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-32">
        <div className="flex flex-col gap-0">
          {SERVICES.map((s, i) => {
            const label = t(`services.${s.key}.label`);
            const tagline = t(`services.${s.key}.tagline`);
            const desc = t(`services.${s.key}.description`);
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-row-${s.slug}`}
                  className="group grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 border-b border-[#F2EBE5]/10 hover:border-[#C5A059]/40 transition-colors"
                >
                  <div className="col-span-12 md:col-span-1">
                    <span className="overline text-[#C5A059]">0{i + 1}</span>
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <h3 className="font-serif-display text-3xl md:text-5xl text-[#F2EBE5] group-hover:text-[#C5A059] transition-colors">
                      {label}
                    </h3>
                    <p className="mt-3 text-sm text-[#A19E9C]">{tagline}</p>
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <p className="text-sm md:text-base text-[#A19E9C] leading-relaxed">
                      {desc}
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-1 flex md:justify-end items-start">
                    <ArrowUpRight
                      className="w-6 h-6 text-[#A19E9C] group-hover:text-[#C5A059] transition-colors"
                      strokeWidth={1.25}
                    />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>
    </PageShell>
  );
};

export default Services;
